/**
 * POST /allocation-questionnaire/submit — hub "Submit Portfolio" (no body).
 *
 * Belongs here: path, axios, retries + min pending + fail toast, mutation hook.
 * Does not belong here: hub card animation phases (see useHubSubmit).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import axiosInstance from '../../../../utils/axiosConfig'
import { mobileBackendUrl } from '../../../../env'
import { useToast } from '../../../../state-management/contexts/ToastContext'
import type { AllocationRiskProfile } from '../types/allocation.types'
import type { QuestionnaireStatus } from '../shared/types/questionnaire.types'
import { withRetries } from './withRetries'
import { awaitWithMinDuration } from './awaitWithMinDuration'
import { refetchQuestionnaireStatusWithRetries } from './useQuestionnaireStatusQuery'

export const SUBMIT_PORTFOLIO_PATH = `${mobileBackendUrl}/v2/portfolio/allocation-questionnaire/submit`
export const SUBMIT_PORTFOLIO_MAX_ATTEMPTS = 3
/** Floor for success path only — failure skips this and toasts immediately. */
export const SUBMIT_PORTFOLIO_MIN_PENDING_MS = 5000
export const SUBMIT_PORTFOLIO_FAILED_TOAST = 'Submitting failed.'

/** POST /submit body — countdown uses `firstPlanDropTime` (epoch ms). */
export interface SubmitPortfolioApiResponse {
  riskProfile?: AllocationRiskProfile | null
  status?: QuestionnaireStatus | null
  firstPlanDropTime?: number | null
}

/** POST portfolio for expert review. No request body. */
export async function submitPortfolioForReview(): Promise<SubmitPortfolioApiResponse> {
  const response = await axiosInstance.post<SubmitPortfolioApiResponse>(SUBMIT_PORTFOLIO_PATH)
  return response?.data ?? {}
}

/**
 * Hub portfolio submit mutation.
 * `submitPortfolio` = retries → status refetch → min pending on success; toast on final fail.
 */
export function useSubmitPortfolioMutation() {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  const mutation = useMutation({
    mutationFn: submitPortfolioForReview,
    retry: false
  })

  async function submitPortfolio(): Promise<SubmitPortfolioApiResponse> {
    try {
      return await awaitWithMinDuration(
        async () => {
          const submitResponse = await withRetries(
            () => mutation.mutateAsync(),
            SUBMIT_PORTFOLIO_MAX_ATTEMPTS
          )
          await refetchQuestionnaireStatusWithRetries(queryClient)
          return submitResponse
        },
        SUBMIT_PORTFOLIO_MIN_PENDING_MS,
        { skipMinOnError: true }
      )
    } catch (error) {
      showToast({ message: SUBMIT_PORTFOLIO_FAILED_TOAST, duration: 2500 })
      throw error
    }
  }

  return {
    ...mutation,
    submitPortfolio
  }
}
